import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Loading from '../common/Loading';
import Api from '../common/Api.js';
import jwt_encode from 'jwt-encode';
import jwt_decode from 'jwt-decode';
import Footer from '../common/Footer';
import Header from '../common/Header';
import FranchiseeAuth from '../common/FranchiseeAuth';
import Select from 'react-select';

// Shared react-select styles — same as Register
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, height: 38 }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

function Profile() {
  const [pageloading, setpageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState({});
  const [errors, setErrors] = useState({});

  // States + cities
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadingCities, setLoadingCities] = useState(false);

  // ---------------------------------------------------------------------------
  // Initial load — profile + categories + states
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      setpageLoading(true);
      const franchisee = JSON.parse(localStorage.getItem('franchisee-token'));

      try {
        // 1. Load profile + categories
        const res = await Api.post(
          'franchisee/profile',
          jwt_encode(franchisee, process.env.REACT_APP_SECRET_KEY)
        );
        const result = jwt_decode(res.data);

        if (result.data.status === 'failure') {
          toast.error(result.data.errors.msg, {
            position: 'bottom-right', autoClose: 5000,
          });
          setpageLoading(false);
          return;
        }

        setCategories(result.data.categories || []);
        const prof = result.data.data || {};
        setProfile(prof);

        // 2. Load states list
        const statesRes = await Api.get('/franchisee/get_form_data');
        const stateList = (statesRes.data.state_list || []).map((s) => ({
          value: s.code ?? s.value,
          label: s.name ?? s.label,
        }));
        setStates(stateList);

        // 3. Preselect state — prefer saved code, fall back to matching the name
        let stateObj = null;
        if (prof.franchisee_state_code) {
          stateObj = stateList.find(
            (s) => String(s.value).toUpperCase() === String(prof.franchisee_state_code).toUpperCase()
          );
        }
        if (!stateObj && prof.franchisee_state) {
          stateObj = stateList.find(
            (s) => String(s.label).toUpperCase() === String(prof.franchisee_state).toUpperCase()
          );
        }
        setSelectedState(stateObj || null);

        // 4. Load cities for that state (if we found one)
        if (stateObj) {
          setLoadingCities(true);
          try {
            const { data: cityRows } = await Api.get('/home/cities', {
              params: { code: stateObj.value },
            });
            const cityList = (cityRows || []).map((c) => ({
              value: c.name ?? c.value,
              label: c.name ?? c.label,
            }));
            setCities(cityList);

            // Preselect city
            if (prof.franchisee_city) {
              const cityObj = cityList.find(
                (c) => String(c.value).toUpperCase() === String(prof.franchisee_city).toUpperCase()
              );
              setSelectedCity(cityObj || { value: prof.franchisee_city, label: prof.franchisee_city });
            }
          } finally {
            setLoadingCities(false);
          }
        }

        setpageLoading(false);
      } catch (err) {
        setpageLoading(false);
        toast.error('Failed to load profile.', { position: 'bottom-right', autoClose: 5000 });
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleChange = (event) => {
    event.persist();
    const { name, value } = event.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleState = async (selected) => {
    setSelectedState(selected);
    setSelectedCity(null);
    setCities([]);

    // Persist into the profile object both code and (later) name
    setProfile((prev) => ({
      ...prev,
      franchisee_state_code: selected?.value || '',
      franchisee_state: selected?.label || '',
      franchisee_city: '',   // reset city
    }));

    if (!selected) return;

    setLoadingCities(true);
    try {
      const { data } = await Api.get('/home/cities', {
        params: { code: selected.value },
      });
      setCities((data || []).map((c) => ({ value: c.name, label: c.name })));
    } catch (err) {
      toast.error('Failed to load cities', { position: 'bottom-right', autoClose: 5000 });
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCity = (selected) => {
    setSelectedCity(selected);
    setProfile((prev) => ({
      ...prev,
      franchisee_city: selected?.value || '',
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    // Make sure state_code + state name are both in the payload
    const payload = {
      ...profile,
      franchisee_state_code: selectedState?.value || profile.franchisee_state_code || '',
      franchisee_state: selectedState?.label || profile.franchisee_state || '',
      franchisee_city: selectedCity?.value || profile.franchisee_city || '',
    };

    try {
      const res = await Api.post(
        'franchisee/update',
        jwt_encode(payload, process.env.REACT_APP_SECRET_KEY)
      );
      const result = jwt_decode(res.data);
      setLoading(false);

      if (result.data.status === 'failure') {
        setErrors(result.data.errors || {});
      } else {
        localStorage.setItem('franchisee-token', JSON.stringify(result.data.data));
        toast.success('Your profile updated successfully !!!', {
          position: 'bottom-right', autoClose: 5000,
        });
      }
    } catch (err) {
      setLoading(false);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <FranchiseeAuth />
      <Header />
      <section className="w3l-faq-main">
        <div className="faq-sec sec-padding">
          <div className="wrapper">
            <div className="row">
              <div className="col-md-12">
                <div className="card card-primary">
                  <div className="card-header">
                    <h3 className="card-title">My Profile</h3>
                  </div>

                  {pageloading ? (
                    <Loading />
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="card-body">
                        <div className="row">

                          {/* Shop Name */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Shop Name</label>
                              <input
                                type="text"
                                name="franchisee_name"
                                value={profile.franchisee_name || ''}
                                onChange={handleChange}
                                className={errors.franchisee_name ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Shop Name"
                              />
                              {errors.franchisee_name && (
                                <span className="error invalid-feedback">{errors.franchisee_name}</span>
                              )}
                            </div>
                          </div>

                          {/* Category */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Category</label>
                              <select
                                name="category_id"
                                onChange={handleChange}
                                value={profile.category_id || ''}
                                className={errors.category_id ? 'form-control is-invalid' : 'form-control'}
                              >
                                <option value="">--select category--</option>
                                {categories.map((category, index) => (
                                  <option key={index} value={category.id}>{category.name}</option>
                                ))}
                              </select>
                              {errors.category_id && (
                                <span className="error invalid-feedback">{errors.category_id}</span>
                              )}
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Email</label>
                              <input
                                type="text"
                                name="email"
                                value={profile.email || ''}
                                onChange={handleChange}
                                className={errors.email ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Email"
                              />
                              {errors.email && (
                                <span className="error invalid-feedback">{errors.email}</span>
                              )}
                            </div>
                          </div>

                          {/* Office No */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Office No</label>
                              <input
                                type="text"
                                name="office_no"
                                value={profile.office_no || ''}
                                onChange={handleChange}
                                className={errors.office_no ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Office No"
                              />
                              {errors.office_no && (
                                <span className="error invalid-feedback">{errors.office_no}</span>
                              )}
                            </div>
                          </div>

                          {/* Aadhaar */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Aadhar No</label>
                              <input
                                type="text"
                                name="aadhar_no"
                                value={profile.aadhar_no || ''}
                                onChange={handleChange}
                                className={errors.aadhar_no ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Aadhar No"
                              />
                              {errors.aadhar_no && (
                                <span className="error invalid-feedback">{errors.aadhar_no}</span>
                              )}
                            </div>
                          </div>

                          {/* PAN */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Pan No</label>
                              <input
                                type="text"
                                name="pan_no"
                                value={profile.pan_no || ''}
                                onChange={handleChange}
                                className={errors.pan_no ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Pan No"
                              />
                              {errors.pan_no && (
                                <span className="error invalid-feedback">{errors.pan_no}</span>
                              )}
                            </div>
                          </div>

                          {/* State — react-select */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>State</label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                name="franchisee_state"
                                value={selectedState}
                                onChange={handleState}
                                options={states}
                                placeholder="Select State"
                                isClearable
                                isSearchable
                              />
                              {errors.franchisee_state && (
                                <span className="error invalid-feedback d-block">{errors.franchisee_state}</span>
                              )}
                            </div>
                          </div>

                          {/* City — react-select, depends on state */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>City</label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                name="franchisee_city"
                                value={selectedCity}
                                onChange={handleCity}
                                options={cities}
                                isLoading={loadingCities}
                                isDisabled={!selectedState}
                                placeholder={selectedState ? 'Select City' : 'Select state first'}
                                isClearable
                                isSearchable
                              />
                              {errors.franchisee_city && (
                                <span className="error invalid-feedback d-block">{errors.franchisee_city}</span>
                              )}
                            </div>
                          </div>

                          {/* Pincode */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Pincode</label>
                              <input
                                type="text"
                                name="franchisee_pincode"
                                value={profile.franchisee_pincode || ''}
                                onChange={handleChange}
                                className={errors.franchisee_pincode ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Pincode"
                              />
                              {errors.franchisee_pincode && (
                                <span className="error invalid-feedback">{errors.franchisee_pincode}</span>
                              )}
                            </div>
                          </div>

                          {/* Address — full width */}
                          <div className="col-md-12">
                            <div className="form-group">
                              <label>Shop Address</label>
                              <textarea
                                name="franchisee_address"
                                value={profile.franchisee_address || ''}
                                onChange={handleChange}
                                className={errors.franchisee_address ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Shop Address"
                                rows={2}
                              />
                              {errors.franchisee_address && (
                                <span className="error invalid-feedback">{errors.franchisee_address}</span>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>

                      <div className="card-footer">
                        <button type="submit" className="btn button-eff" disabled={loading}>
                          <span style={{ marginLeft: '35px' }}>Submit</span>
                          <span style={{ float: 'right', width: '10px', height: '10px', marginLeft: '5px' }}>
                            {loading && <i className="fa fa-circle-o-check fa-spin"></i>}
                          </span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Profile;